/**
 * Stage 1 of the pipeline: resolve feed sources against a single product,
 * in-process and free. Agent sources are resolved later (stage 2, the agent
 * session) and are rejected here. There are no asset/literal arms — non-varying
 * values live in the template markup, not in slots.
 *
 * A missing feed value throws MissingValueError — the product is then excluded
 * from the enriched output and listed in the report (rule 4: absence is an
 * error, never a partial ad). Values substitute verbatim (rule 2).
 */

import { parseFeedBinding } from "./classify";
import {
  MissingValueError,
  type ProductRecord,
  type ResolvedSlotValue,
  type Source,
} from "./types";

/** Read a feed binding's raw value from a product, or null if absent/empty. */
// Generic over the field shape so the editor's URL-backed product record
// (editor-slots.ts) resolves through the same binding grammar as the
// server-side ProductRecord.
export function readFeedValue(
  product: Pick<ProductRecord, "title" | "description" | "userTags">,
  binding: string,
): string | null {
  const parsed = parseFeedBinding(binding);
  if (!parsed) return null;

  if (parsed.kind === "field") {
    return nonEmpty(product[parsed.field]);
  }
  if (parsed.kind === "userTag") {
    return nonEmpty(product.userTags[parsed.key]);
  }
  // Image bindings have no scalar value; callers should use resolveFeedImage.
  return null;
}

/** Resolve a feed image binding to a product image, or null if absent. */
export function resolveFeedImage<I extends { slot: string; primary: boolean }>(
  product: { images: I[] },
  binding: string,
): I | null {
  const parsed = parseFeedBinding(binding);
  if (!parsed || parsed.kind !== "image") return null;

  if (parsed.image === "primary") {
    return (
      product.images.find((img) => img.slot === "primary") ??
      product.images.find((img) => img.primary) ??
      null
    );
  }
  const wanted = `additional-${parsed.image.additional}` as const;
  return product.images.find((img) => img.slot === wanted) ?? null;
}

/**
 * Resolve a single feed source for a product. Throws MissingValueError on
 * absence. Agent sources are resolved by the agent session, not here.
 */
export function resolveSource(
  source: Source,
  product: ProductRecord,
): ResolvedSlotValue {
  if (source.from === "agent") {
    throw new Error(
      'resolveSource cannot resolve an "agent" source — that is the agent stage',
    );
  }
  const parsed = parseFeedBinding(source.binding);
  if (parsed?.kind === "image") {
    const image = resolveFeedImage(product, source.binding);
    if (!image) throw new MissingValueError(source.binding);
    return {
      kind: "image",
      filePath: image.filePath,
      filename: image.filename,
    };
  }
  const value = readFeedValue(product, source.binding);
  if (value === null) throw new MissingValueError(source.binding);
  return { kind: "text", text: value };
}

function nonEmpty(value: string | null | undefined): string | null {
  if (value === null || value === undefined) return null;
  const str = String(value);
  return str.length > 0 ? str : null;
}
